// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// DECISION: Exchange rates are stored as integer × 1,000,000 to achieve
// 6-decimal-place precision without floating-point errors. This handles
// rates like USD/JPY = 149.823456 precisely.
// e.g. 1 EUR = 1.084700 USD → stored as 1084700

import { getCurrencyDecimalPlaces, getCurrencyByCode } from './currencies.js';
import { getDisplayLocale } from './formatLocale.js';
import type { Account, Price, Transaction, Split } from '../types.js';

export const RATE_PRECISION = 1_000_000;

/** Convert an amount from one currency to another using a stored rate. */
export function convertCurrency(
  amount: number,           // in source currency's smallest unit
  fromCurrency: string,
  toCurrency: string,
  rate: number,             // toCurrency per 1 fromCurrency unit × 1,000,000
  fromDecimalPlaces: number,
  toDecimalPlaces: number,
): number {
  if (fromCurrency === toCurrency) return amount;
  const majorAmount = amount / Math.pow(10, fromDecimalPlaces);
  const rateDecimal = rate / RATE_PRECISION;
  const majorResult = majorAmount * rateDecimal;
  return Math.round(majorResult * Math.pow(10, toDecimalPlaces));
}

/** Format a currency amount for display. */
export function formatCurrency(
  amount: number,           // in smallest unit (e.g. cents)
  currencyCode: string,
  options?: {
    showSymbol?: boolean;  // default true
    showCode?: boolean;    // default false
    compact?: boolean;     // e.g. $1.2K instead of $1,200.00
    locale?: string;       // display locale; default: active display locale
  },
): string {
  const decimalPlaces = getCurrencyDecimalPlaces(currencyCode);
  const value = amount / Math.pow(10, decimalPlaces);
  const showSymbol = options?.showSymbol !== false;
  const locale = options?.locale ?? getDisplayLocale();

  if (options?.compact && Math.abs(value) >= 1000) {
    const def = getCurrencyByCode(currencyCode);
    const sym = showSymbol ? (def?.symbol ?? currencyCode) : '';
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    const suffix = abs >= 1_000_000 ? `${(abs / 1_000_000).toFixed(1)}M` : `${(abs / 1000).toFixed(1)}K`;
    return `${sign}${sym}${suffix}`;
  }

  let result: string;
  try {
    if (showSymbol && /^[A-Z]{3}$/.test(currencyCode)) {
      result = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(value);
    } else {
      const def = getCurrencyByCode(currencyCode);
      const sym = showSymbol ? (def?.symbol ?? currencyCode) : '';
      result = sym + new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(value);
    }
  } catch {
    const def = getCurrencyByCode(currencyCode);
    const sym = showSymbol ? (def?.symbol ?? currencyCode) : '';
    const formatted = Math.abs(value).toFixed(decimalPlaces);
    result = (value < 0 ? '-' : '') + sym + formatted;
  }

  if (options?.showCode) result = `${result} ${currencyCode}`;
  return result;
}

/**
 * Get the exchange rate between two currencies on or near a given date.
 * Returns rate × 1,000,000, or null if no rate exists within toleranceDays.
 *
 * DECISION: rates are stored as (fromCurrency → toCurrency), so we look for
 * Price entries where commodityId = fromCurrency and currency = toCurrency.
 * If none found, we try the inverse and compute 1M^2 / inverseRate.
 * No triangulation (no USD→JPY via EUR).
 */
export function getExchangeRate(
  fromCurrency: string,
  toCurrency: string,
  date: string,
  prices: Price[],
  toleranceDays = 7,
): number | null {
  if (fromCurrency === toCurrency) return RATE_PRECISION;

  const target = new Date(date + 'T00:00:00').getTime();
  const toleranceMs = toleranceDays * 86400000;

  const direct = prices.filter(
    (p) => p.commodityId === fromCurrency && p.currency === toCurrency,
  );

  if (direct.length > 0) {
    let best: Price | null = null;
    let bestDiff = Infinity;
    for (const p of direct) {
      const diff = Math.abs(new Date(p.date + 'T00:00:00').getTime() - target);
      if (diff <= toleranceMs && diff < bestDiff) {
        bestDiff = diff;
        best = p;
      }
    }
    return best?.value ?? null;
  }

  // Try inverse: stored as (toCurrency → fromCurrency)
  const inverse = prices.filter(
    (p) => p.commodityId === toCurrency && p.currency === fromCurrency,
  );

  if (inverse.length > 0) {
    let best: Price | null = null;
    let bestDiff = Infinity;
    for (const p of inverse) {
      const diff = Math.abs(new Date(p.date + 'T00:00:00').getTime() - target);
      if (diff <= toleranceMs && diff < bestDiff) {
        bestDiff = diff;
        best = p;
      }
    }
    if (!best) return null;
    // Invert: if 1 EUR = X USD (stored as X × 1M), then 1 USD = (1M/X) EUR (× 1M)
    // inverseRate = 1M × 1M / storedRate
    return Math.round((RATE_PRECISION * RATE_PRECISION) / best.value);
  }

  return null;
}

/** Convert each split of a transaction to home currency. */
export function convertTransactionToHomeCurrency(
  transaction: Transaction,
  accounts: Map<string, Account>,
  homeCurrency: string,
  prices: Price[],
  asOfDate: string,
): Array<{
  splitId: string;
  originalAmount: number;
  originalCurrency: string;
  convertedAmount: number;
  rate: number | null;
  rateDate: string | null;
}> {
  const homeDecimalPlaces = getCurrencyDecimalPlaces(homeCurrency);
  return transaction.splits.map((split) => {
    const account = accounts.get(split.accountId);
    const originalCurrency = account?.currency ?? transaction.currency;
    if (originalCurrency === homeCurrency) {
      return {
        splitId: split.splitId,
        originalAmount: split.amount,
        originalCurrency,
        convertedAmount: split.amount,
        rate: null,
        rateDate: null,
      };
    }
    const fromDecimalPlaces = getCurrencyDecimalPlaces(originalCurrency);
    const rateValue = getExchangeRate(originalCurrency, homeCurrency, asOfDate, prices);
    const convertedAmount = rateValue !== null
      ? convertCurrency(split.amount, originalCurrency, homeCurrency, rateValue, fromDecimalPlaces, homeDecimalPlaces)
      : 0;
    // Find actual rate date used
    const matchedPrice = prices.find(
      (p) => p.commodityId === originalCurrency && p.currency === homeCurrency,
    );
    return {
      splitId: split.splitId,
      originalAmount: split.amount,
      originalCurrency,
      convertedAmount,
      rate: rateValue,
      rateDate: matchedPrice?.date ?? null,
    };
  });
}

/** Calculate currency gain/loss for a foreign-currency account over a period. */
export function calculateCurrencyGainLoss(
  account: Account,
  transactions: Transaction[],
  prices: Price[],
  startDate: string,
  endDate: string,
  homeCurrency: string,
): {
  openingBalance: number;
  closingBalance: number;
  openingValue: number;
  closingValue: number;
  gainLoss: number;
} {
  let openingBalance = 0;
  let closingBalance = 0;

  for (const tx of transactions) {
    if (tx.voided) continue;
    const split = tx.splits.find((s) => s.accountId === account.id);
    if (!split) continue;
    if (tx.date < startDate) openingBalance += split.amount;
    if (tx.date <= endDate) closingBalance += split.amount;
  }

  const fromDecimalPlaces = getCurrencyDecimalPlaces(account.currency);
  const homeDecimalPlaces = getCurrencyDecimalPlaces(homeCurrency);

  const openingRate = getExchangeRate(account.currency, homeCurrency, startDate, prices, 30);
  const closingRate = getExchangeRate(account.currency, homeCurrency, endDate, prices, 30);

  const openingValue = openingRate !== null
    ? convertCurrency(openingBalance, account.currency, homeCurrency, openingRate, fromDecimalPlaces, homeDecimalPlaces)
    : 0;
  const closingValue = closingRate !== null
    ? convertCurrency(closingBalance, account.currency, homeCurrency, closingRate, fromDecimalPlaces, homeDecimalPlaces)
    : 0;

  return { openingBalance, closingBalance, openingValue, closingValue, gainLoss: closingValue - openingValue };
}

/** Find or return a template for a trading account for the given currency. */
export function findTradingAccount(
  currency: string,
  accounts: Account[],
): Account | undefined {
  return accounts.find((a) => a.type === 'TRADING' && a.currency === currency);
}

/**
 * Build the complete set of splits for a currency exchange transaction,
 * including trading account splits.
 *
 * DECISION: trading account IDs must already exist in the accounts array.
 * Callers must create trading accounts via AccountCreated events before calling this.
 */
export function buildCurrencyExchangeSplits(
  fromAccount: Account,
  toAccount: Account,
  fromAmount: number,  // positive: amount leaving fromAccount
  toAmount: number,   // positive: amount arriving in toAccount
  memo: string,
  accounts: Account[],
  generateId: () => string,
): Split[] {
  const tradingFrom = findTradingAccount(fromAccount.currency, accounts);
  const tradingTo = findTradingAccount(toAccount.currency, accounts);

  if (!tradingFrom || !tradingTo) {
    throw new Error(
      `Trading accounts not found for ${fromAccount.currency} or ${toAccount.currency}. ` +
      `Create Trading:${fromAccount.currency} and Trading:${toAccount.currency} accounts first.`,
    );
  }

  return [
    {
      splitId: generateId(),
      accountId: fromAccount.id,
      amount: -fromAmount,
      quantity: -fromAmount,
      reconcileState: 'n',
      reconcileDate: null,
      memo,
      action: '',
    },
    {
      splitId: generateId(),
      accountId: tradingFrom.id,
      amount: fromAmount,
      quantity: fromAmount,
      reconcileState: 'n',
      reconcileDate: null,
      memo,
      action: '',
    },
    {
      splitId: generateId(),
      accountId: tradingTo.id,
      amount: -toAmount,
      quantity: -toAmount,
      reconcileState: 'n',
      reconcileDate: null,
      memo,
      action: '',
    },
    {
      splitId: generateId(),
      accountId: toAccount.id,
      amount: toAmount,
      quantity: toAmount,
      reconcileState: 'n',
      reconcileDate: null,
      memo,
      action: '',
    },
  ];
}
